import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Icon,
  Button,
  Message,
  Popup,
} from "semantic-ui-react";
import { Effluentform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import {
  hasPermission,
  isAdmin,
  isMaster,
  isEditor,
  isReviewer,
} from "../../../../utils/checkPermission";
import "./ListEffluentForms.scss";
import { EffluentForm } from "../EffluentForm";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { formatDateView } from "../../../../utils/formatDate";
import { useLanguage } from "../../../../contexts";

import { determineEffluentUnits } from "../../../../utils/dataAndFormulas/effluentForms";
import {
  convertUnitsFinal as convertUnits,
  effluentCodes,
} from "../../../../utils/dataAndFormulas/effluentForms";
import { formatValue } from "../../../../utils/dataAndFormulas/convert";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
import { Form } from "../../../../api";

const _ = require("lodash");

const effluentFormController = new Effluentform();
const formController = new Form();

export function ListEffluentForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;
  const [effluentforms, setEffluentForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== null) {
        setLoading(true);
        try {
          const response =
            await effluentFormController.getEffluentFormsBySiteAndYear(
              accessToken,
              siteSelected,
              yearSelected,
            );
          if (response.code === 200) {
            setEffluentForms(response.effluentForms);
          } else {
            setEffluentForms([]);
          }
        } catch (error) {
          console.error(error);
          setEffluentForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading)
    return (
      <Loader
        active
        inline="centered"
        content="Cargando datos de efluentes..."
      />
    );

  return (
    <div className="list-effluent-forms">
      <TablePeriods
        data={effluentforms}
        reload={reload}
        onReload={onReload}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    accessToken,
    year,
    site,
    configFormData,
    t,
    language,
    user,
  } = props;

  // Estados existentes
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [unitsRecorded, setUnitsRecorded] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // 🆕 Estados para edición batch
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el modal de configuración de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () =>
    setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () =>
    setShowUpdateUnitsModal((prevState) => !prevState);
  const toggleExpand = (field) =>
    setExpandedFields((prev) => ({ ...prev, [field]: !prev[field] }));

  useEffect(() => {
    (async () => {
      try {
        if (site && year) {
          const fields = determineFields();
          setUniqueFields(fields);
        }
      } catch (error) {
        setUniqueFields([]);
      }
    })();
  }, [configFormData, reload]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};

        // 1. Primero intentar obtener las unidades desde configFormData.fields (Form)
        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
        }

        // 2. Si no hay unidades en configFormData, obtener de los formularios guardados
        // COMENTADO: Esta lógica genera confusión. Ahora solo se usan las unidades de configForm.fields
        // if (Object.keys(recordedUnits).length === 0) {
        //   const response = await effluentFormController.getLastUnitFieldsByYear(
        //     accessToken,
        //     site,
        //     year,
        //     uniqueFields
        //   );

        //   if (response.code === 200) {
        //     recordedUnits = response.fields;
        //   }
        // }

        setUnitsRecorded(recordedUnits);

        // Mostrar advertencia solo si no hay configFormData configurado y hay unidades de formularios
        setShowUnitsWarning(
          !configFormData && Object.keys(recordedUnits).length > 0,
        );

        // 3. Construir selectedUnits basándose solo en lo registrado (sin defaults)
        const values = {};
        uniqueFields.forEach((field) => {
          // Solo usar la unidad si está registrada, no asignar valor por defecto
          values[field] = recordedUnits[field] || "";
        });

        setSelectedUnits(values);

        // 4. Validar campos activos sin unidades en configFormData
        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit,
          );

          if (activeFieldsWithoutUnit.length > 0) {
            setShowMissingUnitsWarning(true);
          } else {
            setShowMissingUnitsWarning(false);
          }
        } else {
          setShowMissingUnitsWarning(false);
        }
      } catch (error) {
        console.error(error);
        setUnitsRecorded({});
        // Si falla, dejar todos los campos vacíos
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData]);

  // 🆕 Alerta al salir con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // 🆕 Atajos de teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        if (hasChanges) {
          handleSaveChanges();
        }
      }
      if (e.key === "Escape" && editMode) {
        handleCancelEdit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [editMode, hasChanges]);

  // Función para verificar si hay al menos una unidad configurada
  const validateUnitsConfigured = () => {
    // Si no hay configFormData o no hay fields, permitir continuar (usará campos por defecto)
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    // Filtrar solo los campos activos
    const activeFields = configFormData.fields.filter(
      (field) => field.active !== false,
    );

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    // Verificar que haya al menos UNA unidad configurada
    const fieldsWithUnit = activeFields.filter(
      (field) => field.unit && field.unit !== "",
    );

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message:
          t("units_not_configured") ||
          "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades.",
      };
    }

    return { valid: true };
  };

  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_effluent_form")} ${formatDateView(form.date)}`,
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  const openUpdateEffluentForm = (period) => {
    const form = data.find((item) => item.period === period);
    setModalContent(
      <EffluentForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        effluentForm={form}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  const openNewEffluentForm = (period) => {
    // Validar que las unidades estén configuradas antes de abrir el formulario
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setModalContent(
      <EffluentForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  const handleUnitsFormSave = (units) => {
    const hasExistingUnits = configFormData?.fields?.some(f => f.unit);
    if (hasExistingUnits) {
      setPendingUnitsToSave(units);
      setShowLegacyUnitWarning(true);
    } else {
      handleSaveUnits(units);
    }
  };

  const handleSaveUnits = async (units) => {
    try {
      // Si no hay configFormData, crear un nuevo Form con todos los campos de efluentes
      if (
        !configFormData?._id ||
        !configFormData?.fields ||
        configFormData.fields.length === 0
      ) {
        // Lista de todos los campos de efluentes con sus códigos
        const allEffluentFieldsData = [
          { name: "domestic_effluents", code: "Efl1" },
          { name: "industrial_effluents", code: "Efl2" },
          { name: "combined_effluents", code: "Efl3" },
          { name: "sludge_and_mud", code: "Efl4" },
          { name: "recycled_effluents", code: "Efl5" },
          { name: "treatment_cost_domestic_effluents", code: "Efl6" },
          { name: "industrial_effluent_treatment_cost", code: "Efl7" },
          { name: "combined_treatment_cost", code: "Efl8" },
          { name: "sludge_treatment_cost", code: "Efl9" },
        ];

        // Crear fields con las unidades seleccionadas
        const fieldsToCreate = allEffluentFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        // Crear el nuevo Form
        const newFormData = {
          module: "effluent",
          site: site,
          fields: fieldsToCreate,
        };

        const response = await formController.createForm(
          accessToken,
          newFormData,
        );

        if (response.code === 200 || response.code === 201) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false); // Ocultar advertencia
          setUnitsRecorded(units);
          setSelectedUnits(units);
          onReload(); // Recargar para obtener el nuevo Form
        } else {
          toast.error(t("error_updating_units"));
        }
        return;
      }

      // Si ya existe configFormData, actualizar las unidades
      const formId = configFormData._id;
      const currentFields = configFormData.fields || [];

      // Solo se envían los campos que cambian de unidad, de forma atómica
      // (ver updateFormFields en el controlador) — no se pisa el documento
      // entero, para no perder un toggle de 'active' guardado desde
      // Fuentes casi al mismo tiempo.
      const changes = currentFields
        .filter((field) => Object.prototype.hasOwnProperty.call(units, field.name))
        .map((field) => ({ name: field.name, unit: units[field.name] }));

      const response =
        changes.length > 0
          ? await formController.updateFormFields(accessToken, formId, changes)
          : { code: 200 };

      if (response.code === 200 || response.code === 201) {
        toast.success(t("units_updated_successfully"));
        setShowUnitsWarning(false); // Ocultar advertencia
        setUnitsRecorded(units);
        setSelectedUnits(units);
        onReload();
      } else {
        toast.error(t("error_updating_units"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_units"));
    }
  };

  // Función para aplicar unidades a formularios existentes (modal separado)
  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response = await effluentFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply,
      );

      if (response.code === 200) {
        toast.success(
          `${t("units_applied_to_forms") || "Unidades aplicadas a"} ${response.updated} ${t("forms") || "formularios"}`,
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_units_to_forms") || "Error al aplicar unidades",
        );
      }
    } catch (error) {
      console.error("Error applying units:", error);
      toast.error(
        t("error_applying_units_to_forms") || "Error al aplicar unidades",
      );
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      const response = await effluentFormController.deleteEffluentForm(
        accessToken,
        dataDeleted._id,
      );
      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true);
      setDataDelete("");
      onOpenCloseConfirm();
      onReload();
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true);
    }
  };

  const periods = PERIODS;

  const determineFields = () => {
    const fields = new Set();
    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(effluentCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active,
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(effluentCodes).forEach((key) => {
        fields.add(key);
      });
    }
    return Array.from(fields);
  };

  const hasDataPeriod = (period) => {
    const existsPeriod = data.some((entry) => entry.period === period);
    return existsPeriod;
  };

  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  // La conversión de unidades permite transformar valores entre diferentes unidades de medida (ej. litros a metros cúbicos).
  // Para mantener las unidades fijas en la tabla, no se convierten los valores; se muestran tal como están registrados.
  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => item[field]?.value || 0);
    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const getUnitName = (field) => {
    const unitValue = normalizeUnit(selectedUnits[field]);

    // Si no hay unidad configurada, mostrar mensaje indicativo
    if (!unitValue || unitValue === "") {
      return "- - -";
    }

    const unitsArray = determineEffluentUnits(field);
    const unitObject = unitsArray.find((u) => u.value === unitValue);
    return unitObject?.name || unitValue;
  };

  // 🆕 Manejar cambio en celda editable
  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    const originalValue = item?.[field]?.value || null;

    // Si el valor es igual al original, remover del objeto de ediciones
    if (newValue === originalValue) {
      const updatedEdits = { ...editedValues };
      delete updatedEdits[key];
      setEditedValues(updatedEdits);
      setHasChanges(Object.keys(updatedEdits).length > 0);
      return;
    }

    // Agregar/actualizar edición
    setEditedValues({
      ...editedValues,
      [key]: {
        formId: item?._id || null,
        period: period,
        field: field,
        value: newValue,
        unit: selectedUnits[field],
        originalValue: originalValue,
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    });
    setHasChanges(true);
  };

  // 🆕 Guardar cambios (batch update)
  const handleSaveChanges = async () => {
    if (!hasChanges) return;

    try {
      setIsSaving(true);

      // Agrupar cambios por tipo: updates vs creates
      const updates = [];
      const creates = [];

      Object.values(editedValues).forEach((change) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: effluentCodes[change.field], // Agregar código del campo
          });
        } else {
          updates.push({
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: effluentCodes[change.field], // Agregar código del campo
          });
        }
      });

      // Enviar batch update al backend
      const response = await effluentFormController.batchUpdateEffluentForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id, // Agregar ID del usuario creador
        },
      );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated} formularios actualizados, ${response.created} creados`,
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error("❌ Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving batch changes:", error);
      toast.error("❌ Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  // 🆕 Cancelar edición
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  // Verificar si hay al menos un período cargado
  const hasLoadedData = data && data.length > 0;

  return (
    <>
      {/* 🆕 Botones de control */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
        <div className="batch-actions">
          {!editMode ? (
            <Popup
              content={t("enable_multiple_edit")}
              trigger={
                <Button
                  className="batch-btn primary"
                  icon
                  //labelPosition="left"
                  onClick={() => setEditMode(true)}
                  disabled={!hasLoadedData}
                >
                  <Icon name="edit" />
                  {/* Editar Múltiples Períodos */}
                </Button>
              }
            />
          ) : (
            <>
              <Button
                className="batch-btn success"
                onClick={handleSaveChanges}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              >
                <Icon name="save" />
                {t("save_changes") || "Guardar Cambios"}
                {hasChanges && ` (${Object.keys(editedValues).length})`}
              </Button>
              <Button
                className="batch-btn cancel"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                <Icon name="cancel" />
                {t("cancel") || "Cancelar"}
              </Button>
              {hasChanges && (
                <span
                  style={{
                    color: "#f57c00",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  <Icon name="warning sign" />
                  {Object.keys(editedValues).length}{" "}
                  {t("pending_changes") || "cambio(s) pendiente(s)"}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* 🆕 Mensaje de cambios pendientes */}
      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {/* 🆕 Advertencia de unidades tomadas de formularios */}
      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una
            mejor consistencia, se recomienda configurar las unidades
            explícitamente haciendo clic en el ícono de tres puntos (⋯) en la
            columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-effluent-forms__table-wrapper">
        <Table className="effluent-list-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                textAlign="center"
                className="sticky-col-1 pill-left-start"
              >
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center" className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center" className="pill-left-end">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_units_in_forms") ||
                            "Modificar unidades en formularios"
                          }
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell
                colSpan="3"
                textAlign="center"
                className="pill-right"
              >
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {uniqueFields
              ? uniqueFields.map((field) => {
                  const isExpanded = expandedFields[field];
                  const { total, average } = calculateTotalAndAverage(field);
                  const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });
                  return (
                    <React.Fragment key={field}>
                      {/* Fila resumen */}
                      <Table.Row>
                        <Table.Cell className="sticky-col-1 pill-left-start">
                          {effluentCodes[field]}
                        </Table.Cell>
                        <Table.Cell className="sticky-col-2">
                          {t(field)}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="center"
                          className="pill-left-end"
                        >
                          {getUnitName(field)}
                          {hasMismatchedUnit && (
                            <Popup
                              content="Hay meses con unidades diferentes a la configurada"
                              trigger={
                                <Icon
                                  name="warning sign"
                                  color="orange"
                                  size="small"
                                  style={{ cursor: "pointer", marginLeft: "4px" }}
                                />
                              }
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell className="pill-gap" />
                        <Table.Cell colSpan="3" className="pill-right">
                          <div className="summary-flex">
                            <span className="summary-item">
                              {t("total")}: <strong>{total}</strong>
                            </span>
                            <span className="summary-item">
                              {t("average")}: <strong>{average}</strong>
                            </span>
                            <Button
                              icon
                              size="tiny"
                              className="expand-btn"
                              type="button"
                              onClick={() => toggleExpand(field)}
                            >
                              <Icon
                                name={
                                  isExpanded ? "chevron up" : "chevron down"
                                }
                              />
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>

                      {/* Fila de cabecera de meses (expandible) */}
                      {isExpanded && (
                        <Table.Row className="months-header-row">
                          <Table.Cell
                            colSpan="7"
                            style={{ padding: 0 }}
                            className="months-full-row"
                          >
                            <div className="months-flex-container">
                              {periods.map((period) => (
                                <div
                                  key={period}
                                  className="month-flex-cell month-flex-header"
                                >
                                  {t(period).slice(0, 3)}
                                  {!editMode &&
                                    (hasDataPeriod(period) ? (
                                      <Dropdown
                                        inline
                                        size="tiny"
                                        icon="ellipsis vertical"
                                        floating
                                        className="icon"
                                      >
                                        <Dropdown.Menu>
                                          <Dropdown.Item
                                            text={t("edit")}
                                            icon={
                                              <Icon
                                                name="edit"
                                                style={{ color: "gray" }}
                                              />
                                            }
                                            onClick={() =>
                                              openUpdateEffluentForm(period)
                                            }
                                          />
                                          <Dropdown.Item
                                            text={t("delete")}
                                            icon={
                                              <Icon
                                                name="trash"
                                                style={{ color: "red" }}
                                              />
                                            }
                                            onClick={() =>
                                              handleDeleteModal(period)
                                            }
                                          />
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    ) : (
                                      <Dropdown
                                        link
                                        size="tiny"
                                        icon="ellipsis vertical"
                                        floating
                                        className="icon"
                                      >
                                        <Dropdown.Menu>
                                          {isAdmin(user.role) ||
                                          isEditor(user.role) ||
                                          isMaster(user.role) ? (
                                            <Dropdown.Item
                                              text={t("load_data")}
                                              icon="plus"
                                              onClick={() =>
                                                openNewEffluentForm(period)
                                              }
                                            />
                                          ) : null}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    ))}
                                </div>
                              ))}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      )}

                      {/* Fila de datos de meses (expandible) */}
                      {isExpanded && (
                        <Table.Row className="months-data-row">
                          <Table.Cell
                            colSpan="7"
                            style={{ padding: 0 }}
                            className="months-full-row"
                          >
                            <div className="months-flex-container">
                              {periods.map((period) => {
                                const item = data.find(
                                  (d) => d.period === period,
                                );
                                return (
                                  <EditableCell
                                    key={`${field}-${period}`}
                                    field={field}
                                    period={period}
                                    item={item}
                                    editMode={editMode}
                                    selectedUnits={selectedUnits}
                                    editedValues={editedValues}
                                    onChange={handleCellChange}
                                    formatNumberWithThousands={
                                      formatNumberWithThousands
                                    }
                                    convertUnits={convertUnits}
                                    isReviewerUser={isReviewer(user.role)}
                                    asFlexCell
                                  />
                                );
                              })}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </React.Fragment>
                  );
                })
              : null}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer show={showModal} close={onOpenCloseModal}>
        {modalContent}
      </BasicDrawer>
      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
        danger
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>
              Por favor configure las unidades haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades.
            </p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => {
          setShowWarningModal(false);
          onOpenCloseUnitsModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />
    </>
  );
}

// 🆕 Componente de Celda Editable
function EditableCell({
  field,
  period,
  item,
  editMode,
  selectedUnits,
  editedValues,
  onChange,
  formatNumberWithThousands,
  convertUnits,
  isReviewerUser,
  asFlexCell,
}) {
  const key = `${period}_${field}`;
  const isEdited = editedValues[key] !== undefined;

  // Valor para mostrar (sin conversión para mantener unidades fijas)
  const displayValue = isEdited
    ? editedValues[key].value
    : item?.[field]?.value || "";

  const [localValue, setLocalValue] = useState(displayValue);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Actualizar local value cuando cambie displayValue
  useEffect(() => {
    setLocalValue(displayValue);
    setHasInteracted(false);
  }, [displayValue]);

  const handleFocus = (e) => {
    // Si el valor es 0 y el usuario no ha interactuado aún, limpiar el campo
    if (!hasInteracted && (localValue === 0 || localValue === "0")) {
      setLocalValue("");
    }
    setHasInteracted(true);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);

    // Si el campo está vacío, enviar 0
    const newValue = inputValue === "" ? 0 : parseFloat(inputValue) || 0;
    onChange(field, period, item, newValue);
  };

  // Verificar si el campo tiene unidad configurada
  const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";

  // Verificar si las unidades no coinciden (item tiene unidad diferente a la configurada)
  const unitMismatch =
    item?.[field]?.unit && hasUnit && normalizeUnit(item[field].unit) !== normalizeUnit(selectedUnits[field]);

  if (!editMode) {
    const content =
      item && item[field] ? (
        <div>
          {formatNumberWithThousands(item[field].value)}
          {unitMismatch && (
            <span
              style={{
                fontSize: "0.75em",
                color: "#f57c00",
                marginLeft: "4px",
                fontWeight: "bold",
              }}
              title={`Unidad registrada: ${item[field].unit} | Unidad configurada: ${selectedUnits[field]}`}
            >
              ({item[field].unit})
            </span>
          )}
        </div>
      ) : (
        "-"
      );
    if (asFlexCell) {
      return <div className="month-flex-cell month-flex-data">{content}</div>;
    }
    return <Table.Cell textAlign="right">{content}</Table.Cell>;
  }

  const inputContent = (
    <>
      <input
        type="number"
        step="0.01"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={!hasUnit || isReviewerUser}
        style={{
          width: "100%",
          height: "30px",
          border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
          borderRadius: "4px",
          padding: "4px 6px",
          textAlign: "right",
          fontSize: "11px",
          cursor: hasUnit && !isReviewerUser ? "text" : "not-allowed",
          backgroundColor: hasUnit ? "white" : "#f5f5f5",
          color: !hasUnit ? "#999" : isReviewerUser ? "#000" : "inherit",
          MozAppearance: "textfield",
        }}
        placeholder="-"
        title={!hasUnit ? "Debe configurar la unidad primero" : ""}
      />
      {isEdited && (
        <Icon
          name="edit"
          size="small"
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            color: "#fbc02d",
          }}
        />
      )}
      <style>{`
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>
    </>
  );

  if (asFlexCell) {
    return (
      <div
        className="month-flex-cell month-flex-data"
        style={{
          position: "relative",
          backgroundColor: isEdited ? "#fff9c4" : undefined,
        }}
      >
        {inputContent}
      </div>
    );
  }

  return (
    <Table.Cell
      textAlign="right"
      style={{
        backgroundColor: isEdited
          ? "#fff9c4"
          : hasUnit
            ? "transparent"
            : "#f5f5f5",
        position: "relative",
        padding: "2px",
      }}
    >
      {inputContent}
    </Table.Cell>
  );
}
